const NUL = new RegExp(String.fromCodePoint(0x00), "g");

/**
 * No `u` flag on purpose: without it the pattern reads UTF-16 code units, which
 * is the unit a lone surrogate exists in. A high half not followed by a low
 * half, or a low half not preceded by a high one.
 */
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

const REPLACEMENT_CHARACTER = String.fromCodePoint(0xfffd);

/**
 * `value` with the two things Postgres cannot store removed: every U+0000, and
 * every lone UTF-16 surrogate.
 *
 * Both are legal in a JavaScript string and in a JSON request body, so a caller
 * who sends them has broken no contract — and both make the WHOLE write fail,
 * not one column, as an opaque 500:
 *
 *   - U+0000 has no representation in `text`, and `jsonb` refuses the `\u0000`
 *     escape outright — `22P05 unsupported Unicode escape sequence`, which
 *     Prisma surfaces as `PrismaClientUnknownRequestError`.
 *   - A lone surrogate has no UTF-8 encoding at all. Prisma serializes its
 *     request as JSON, the query engine's `serde_json` refuses the escape
 *     (`unexpected end of hex escape`), and `text` could not hold it anyway.
 *
 * NUL is DROPPED, which is what the document processors' own `sanitizeText`
 * already does before chunking, so stored text and indexed text agree. A lone
 * surrogate becomes U+FFFD, the replacement character every UTF-8 encoder emits
 * for it, so the position of the damage stays visible to a reader.
 *
 * Use this where text crosses a trust boundary into a column. Inside the
 * process, `truncateToCodeUnits` keeps a cut from CREATING a lone surrogate and
 * deliberately repairs nothing; the two are complementary.
 */
export function toPostgresStorableText(value: string): string {
  return value.replace(NUL, "").replace(LONE_SURROGATE, REPLACEMENT_CHARACTER);
}

/** The refusal for a URL that carries what Postgres cannot store. */
export const STORABLE_URL_RULE =
  "url must not contain a NUL (U+0000) or an unpaired surrogate, which cannot be stored";

/** True when Postgres can store `value` as it is. */
export function isPostgresStorableText(value: string): boolean {
  return toPostgresStorableText(value) === value;
}
